// how-to-start/utils/template-outline-seed.ts
//
// WHAT THE TEMPLATE'S SLIDES ARE, in words the outline generator can use.
//
// "Create with AI" on a template is not "generate a deck". The user picked
// these slides, in this order, and the deck being built has exactly them --
// so the outline has to be written FOR them: a slide that holds three cards
// needs three items, a slide with a chart needs figures to chart, and slide
// one is a cover.
//
// The template's own story JSON is already fetchable on the frontend (see
// ./fetch-template-story), and it is the same entity tree the editor uses --
// slide > combo > set > group > element -- so the kinds are read off the
// structure rather than from any metadata the template may or may not carry.
//
// The result is a few lines of text, which is all the prompt needs. See
// dotell-backend wizard/template-slides-prompt.js for the other half.

/** One slide of the template, as the outline generator will see it. */
export type TemplateSlideSeed = {
  /** 1-based, matching the outline item it will be paired with. */
  position: number;
  /** "cover", "metrics", "timeline", "cards", "chart", "quote", "text"... */
  kind: string;
  /** How many repeating items the slide holds; 0 when it does not repeat. */
  items: number;
  /** What the slide carries, for a brief that has to fill it. */
  holds: string[];
  /** The template's own title, as a hint at what the slide is for. */
  title: string;
  /**
   * A few words of the copy under the title -- a clue to the slide's PURPOSE
   * where the heading alone gives none ("01"), never text to reuse. Capped at
   * GLIMPSE_MAX so the template's filler cannot drown out the user's subject.
   * See dotell-backend wizard/template-slides-prompt.js.
   */
  glimpse?: string;
};

/** How much of a slide's copy its line carries, at most. */
const GLIMPSE_MAX = 100;

/** The copy after the heading, short, with quotes the line can carry. */
const glimpseOf = (texts: string[]): string =>
  texts.slice(1).join(" / ").slice(0, GLIMPSE_MAX).replace(/"/g, "'").trim();

type Node = {
  type?: string;
  settings?: Record<string, unknown> & { name?: string; content?: unknown };
  children?: unknown;
};

const asNodes = (children: unknown): Node[] =>
  Array.isArray(children) ? (children as Node[]) : [];

const textOf = (html: unknown): string =>
  String(html ?? "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();

type Tally = {
  combos: string[];
  sets: number;
  elements: Record<string, number>;
  texts: string[];
};

const tally = (node: unknown, out: Tally): Tally => {
  if (!node || typeof node !== "object") return out;
  const n = node as Node;
  const settings = n.settings ?? {};
  const name = typeof settings.name === "string" ? settings.name : "";

  if (n.type === "combo") out.combos.push(name || "?");
  if (n.type === "set") out.sets += 1;
  if (name) out.elements[name] = (out.elements[name] ?? 0) + 1;
  if ((name === "RichText" || name === "CollapsibleText") && settings.content) {
    const text = textOf(settings.content);
    if (text) out.texts.push(text);
  }

  for (const child of asNodes(n.children)) tally(child, out);
  return out;
};

/**
 * The slide's kind, from its combo and what it holds.
 *
 * Ordered most specific first: a timeline that also has images is still a
 * timeline, and a cover with a chart on it is still a cover.
 */
const kindOf = (t: Tally, position: number): string => {
  const combos = t.combos.join(" ");
  const has = (name: string) => (t.elements[name] ?? 0) > 0;

  if (/VTimeline|Timeline/i.test(combos)) return "timeline";
  if (/Carousel/i.test(combos)) return "carousel";
  if (/Tabs/i.test(combos)) return "tabs";
  if (/Narrator/i.test(combos)) return "narrator";
  // A cover is the first slide of a deck, and it looks like one: a logo, or a
  // full-screen combo carrying little more than a title.
  if (position === 1 && (has("Logo") || /FullScreen/i.test(combos))) {
    return "cover";
  }
  if (has("Chart")) return "chart";
  if (has("Table") || has("Grid")) return "table";
  if (has("AnimatedNumbers")) return "metrics";
  if (has("Icon") && /Array/i.test(combos)) return "cards with icons";
  if (/Array/i.test(combos)) return "cards";
  if (has("Image")) return "text with an image";
  return "text";
};

/** Repeating items on the slide: cards, steps, tabs. Not the title block. */
const itemsIn = (t: Tally, kind: string): number => {
  if (["text", "cover", "text with an image"].includes(kind)) return 0;
  // A slide's sets are its title block plus one per item; a lone set is the
  // title block only.
  const fromSets = t.sets > 1 ? t.sets - 1 : 0;

  // COUNT THE THINGS, NOT THE BOXES. A layout that nests one array inside
  // another has more sets than it has cards -- a real metrics slide with four
  // figures counted five. Where the slide repeats a visible element, that
  // element IS the count.
  const repeating = Math.max(
    t.elements.AnimatedNumbers ?? 0,
    t.elements.Icon ?? 0,
    t.elements.Chart ?? 0,
    t.elements.Image ?? 0,
  );
  return repeating > 0 ? repeating : fromSets;
};

const holdsIn = (t: Tally): string[] => {
  const held: string[] = [];
  const n = (name: string) => t.elements[name] ?? 0;
  if (n("Chart")) held.push(n("Chart") > 1 ? `${n("Chart")} charts` : "a chart");
  if (n("AnimatedNumbers")) held.push(`${n("AnimatedNumbers")} big numbers`);
  if (n("Icon")) held.push(`${n("Icon")} icons`);
  if (n("Image")) held.push(n("Image") > 1 ? `${n("Image")} images` : "an image");
  if (n("Logo")) held.push("a logo");
  // A CALL TO ACTION NEEDS SOMEWHERE TO GO. Without this the line said
  // nothing about buttons, so the outline generator wrote "Visit our website
  // / Contact Our Team" onto a closing slide that has neither -- copy with
  // nowhere to land, on the outline the user reviews and approves.
  if (n("Button")) held.push(n("Button") > 1 ? `${n("Button")} buttons` : "a button");
  return held;
};

/**
 * The copy on one slide, in reading order and stripped of markup: the heading
 * first, then whatever follows it.
 */
export const slideTexts = (slide: unknown): string[] =>
  tally(slide, { combos: [], sets: 0, elements: {}, texts: [] }).texts;

/** Every slide of a template story, described. */
export const templateOutlineSeed = (story: unknown): TemplateSlideSeed[] => {
  const slides = (story as { slides?: unknown })?.slides;
  if (!Array.isArray(slides)) return [];

  return slides.map((slide, index) => {
    const t = tally(slide, { combos: [], sets: 0, elements: {}, texts: [] });
    const position = index + 1;
    const kind = kindOf(t, position);
    return {
      position,
      kind,
      items: itemsIn(t, kind),
      holds: holdsIn(t),
      // The FIRST text, which is the heading: slides are written heading
      // first. Taking the longest instead picked up body paragraphs -- "Our
      // mission is to empower businesses worldwide by..." where the slide is
      // simply called "Our Mission" -- and a paragraph of the template's
      // filler copy drowns out the user's own subject.
      title: t.texts[0] ?? "",
      glimpse: glimpseOf(t.texts),
    };
  });
};

/** The line's title part, with its glimpse when it has one. */
export const titleWithGlimpse = (title: string, glimpse?: string): string =>
  glimpse
    ? `template title: "${title.slice(0, 80)}" — its copy reads: "${glimpse.replace(/"/g, "'")}"`
    : `template title: "${title.slice(0, 80)}"`;

/** The seed as the lines the prompt reads. Empty string when there is none. */
export const formatTemplateSeedForPrompt = (
  seed: TemplateSlideSeed[],
): string =>
  seed
    .map((s) => {
      const parts = [`${s.position}. ${s.kind}`];
      if (s.items > 0) parts.push(`${s.items} items`);
      if (s.holds.length) parts.push(`holds ${s.holds.join(", ")}`);
      // Trimmed: a whole paragraph of the template's filler copy would drown
      // out the user's own subject.
      if (s.title) parts.push(titleWithGlimpse(s.title, s.glimpse));
      return parts.join(" — ");
    })
    .join("\n");
